import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Formik, Form, useFormikContext } from 'formik'
import styled from 'styled-components'
import RadioButtonField from 'features/forms/components/RadioButtonField'
import InfoTooltip from 'components/InfoTooltip'
import { SpaceType, UpdateMapItem } from 'generated/graphql'
import { allSpacesSelector } from 'features/events/eventSlice'
import { showNotification } from 'features/notifications'
import widgets from 'features/map/widgets'
import TextField from 'features/forms/components/TextField'
import SelectField from 'features/forms/components/SelectField'
import { _ } from 'utils'
import { deleteMapItem, updateMapItem } from 'features/map/graphql/mutation'
import Button from 'components/Button'
import Icon from 'components/Icon'
import FullPageLoading from 'components/FullPageLoading'
import { DrawItem } from '../types'

const S = {
    EditMapItemForm: styled(Form)`
        margin: 0;
    `,
    Header: styled.div`
        display: flex;
        justify-content: space-between;
    `,
    Title: styled.h4`
        font-weight: 500;
        font-size: 18px;
        line-height: 21px;
        align-items: center;
        color: #4E5155;
    `,
    Actions: styled.div`
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-top: 32px;
    `,
    RadioButtonField: styled(RadioButtonField)`
        margin-top: 10px;
        margin-bottom: 0;
        padding: 0;
    `,
    SelectField: styled(SelectField)`
        margin-top: -16px;
        margin-bottom: 0;
    `,
    TextField: styled(TextField)`
        margin-top: -10px;
        margin-bottom: 0;
    `
}

const convertSpaceForSelection = (space: SpaceType) => ({
    id: space.id,
    name: `${space.name} (${space.type})`
})

export type SelectionType = 'spaces' | 'widget'
export interface UpdateItem extends UpdateMapItem {
    type?: SelectionType
}

export const FormContent = () => {
    const { values, setFieldValue } = useFormikContext<UpdateItem>()
    const spaces = useSelector(allSpacesSelector)

    const widgetSelectOptions = useMemo(() => widgets(), [])
    const spaceSelectOptions = spaces.map(convertSpaceForSelection)

    useEffect(() => {
        if (values.type === 'spaces') {
            setFieldValue('widget', undefined)
            setFieldValue('embeddedUrl', null)
            setFieldValue('widgetLink', null)
        }
        if (values.type === 'widget') {
            if (!widgetSelectOptions.map((e) => e.id).includes(values.widget)) {
                setFieldValue('widget', null)
            }
            setFieldValue('spaceId', null)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.type])

    return (
        <>
            <TextField
                name="name"
                placeholder={_('map_editor.form.name')}
                className="mb-0 px-0"
            />
            <div className="flex-space-between mt-4 w-100">
                <S.RadioButtonField
                    name="type"
                    options={[
                        { label: _('map_editor.form.space_type'), value: 'spaces' },
                        { label: _('map_editor.form.widget_type'), value: 'widget' }
                    ]}
                    defaultValue="spaces"
                />
                <InfoTooltip
                    text={_('map_editor.form.select_space_or_widget_helper')}
                    size={24}
                />
            </div>
            {values.type === 'spaces' ? (
                <S.SelectField
                    name="spaceId"
                    options={spaceSelectOptions}
                    optionValue="id"
                    optionLabel="name"
                    className="px-0"
                    placeholder={_('map_editor.form.select_space.placeholder')}
                    isClearable={false}
                />
            ) : (
                <S.SelectField
                    name="widget"
                    options={widgetSelectOptions}
                    optionValue="id"
                    optionLabel="name"
                    className="px-0"
                    placeholder={_('map_editor.form.select_widget.placeholder')}
                    isClearable={false}
                />
            )}
            {values.widget === 'embedded' && (
                <S.TextField
                    name="embeddedUrl"
                    placeholder={_('map_editor.form.embedded_url')}
                    className="px-0"
                />
            )}
            {values.widget === 'external_link' && (
                <S.TextField
                    name="widgetLink"
                    placeholder={_('map_editor.form.widget_link')}
                    className="px-0"
                />
            )}
        </>
    )
}

const convertDataForForm = (data: Partial<DrawItem>): Partial<UpdateItem> => {
    if (data.space) {
        data.widget = null
    }

    let type: SelectionType = 'spaces'
    if (data.widget) {
        type = 'widget'
    }

    return ({
        name: data.title,
        spaceId: data?.space?.id,
        widget: data.widget,
        widgetLink: data.widgetLink,
        embeddedUrl: data.embeddedUrl,
        type
    })
}

const EditMapItemForm = ({
    drawItem, close, callGetMapItems
}: { drawItem: Partial<DrawItem>, close: () => void, callGetMapItems: () => void }) => {
    const [isUploading, setIsUploading] = useState(false)
    const dispatch = useDispatch()
    const deleteItem = async () => {
        setIsUploading(true)
        await deleteMapItem(drawItem.generatedId)
        await callGetMapItems()
        setIsUploading(false)
        close()
    }

    const handleSubmit = async (values: UpdateItem) => {
        const submitValues = { ...values }
        if (!submitValues.widget && !submitValues.spaceId) {
            dispatch(showNotification({
                type: 'warning',
                body: _('map_editor.form.area_no_space_widget'),
                displayDuration: 3000,
            }))
            return
        }
        delete submitValues.type
        setIsUploading(true)
        await updateMapItem(drawItem.generatedId, submitValues)
        await callGetMapItems()
        setIsUploading(false)
        close()
    }

    if (!drawItem) {
        return
    }

    return (
        <Formik
            initialValues={convertDataForForm(drawItem)}
            onSubmit={handleSubmit}
        >
            <>
                <S.EditMapItemForm>
                    <S.Header>
                        <S.Title>{_('map_editor.form.edit_area_title')}</S.Title>
                        <span>
                            <Icon icon="close" size={22} onClick={close} className="ml-2" />
                        </span>
                    </S.Header>
                    <FormContent />
                    <S.Actions>
                        <Button variant="danger" onClick={deleteItem}>{_('button.remove')}</Button>
                        <div className="d-flex">
                            <Button variant="secondary" onClick={close}>{_('button.cancel')}</Button>
                            <Button variant="success" type="submit" className="ml-2">
                                {_('button.save')}
                            </Button>
                        </div>
                    </S.Actions>
                </S.EditMapItemForm>
                {isUploading && <FullPageLoading isAbsolute fullWidth />}
            </>
        </Formik>
    )
}

export default React.memo(EditMapItemForm)
